import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";
import {
  setupWaveform,
  setupCircularPlayer,
} from "/WebInterface/new-ui/modules/web-components/audio-player/players.js";

// Base class for audio players
class BaseAudio extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.renderSkeleton();
  }

  renderSkeleton() {
    this.shadowRoot.innerHTML = `
      <div id="player-container" class="skeleton skeleton-rect" data-rect-h="100%" data-c-w="100%" data-c-p="0%" data-lines="0" data-bg="var(--bg-skeleton)"></div>
    `;
    // Apply CSS custom properties from data attributes (violates CSP but necessary for skeleton animations)
    const skeletonEl = this.shadowRoot.querySelector('.skeleton');
    if (skeletonEl) {
      if (skeletonEl.dataset.rectH) skeletonEl.style.setProperty('--rect-h', skeletonEl.dataset.rectH);
      if (skeletonEl.dataset.cW) skeletonEl.style.setProperty('--c-w', skeletonEl.dataset.cW);
      if (skeletonEl.dataset.cP) skeletonEl.style.setProperty('--c-p', skeletonEl.dataset.cP);
      if (skeletonEl.dataset.lines) skeletonEl.style.setProperty('--lines', skeletonEl.dataset.lines);
      if (skeletonEl.dataset.bg) skeletonEl.style.setProperty('--bg', skeletonEl.dataset.bg);
    }
  }

  connectedCallback() {
    const src = this.getAttribute("src");
    const autonext =
      this.getAttribute("autonext") === "true" ||
      this.getAttribute("dir") === "true";
    const playerContainer = this.shadowRoot.querySelector("#player-container");

    this.setupPlayer(playerContainer, src, autonext);

    playerContainer.classList.remove("skeleton", "skeleton-rect");
    this.shadowRoot.adoptedStyleSheets = [this.responsiveStyles()];
  }

  setupPlayer(playerContainer, src, autonext) {
    // To be implemented by subclasses
  }

  responsiveStyles() {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(`
      :host {
        display: block;
        width: 100%;
        height: 100%;
      }
      #player-container {
        width: 100%;
        height: 100%;
      }
    `);
    return sheet;
  }
}

// Waveform audio player
class WAudio extends BaseAudio {
  setupPlayer(playerContainer, src, autonext) {
    const { waveSurfer } = setupWaveform(playerContainer, src, {
      height: this.getAttribute("height") || 25,
      autonext,
    });
    this.player = waveSurfer;
  }
}

// Circular audio player
class WAudioCircular extends BaseAudio {
  setupPlayer(playerContainer, src, autonext) {
    const { audio } = setupCircularPlayer(playerContainer, src, { autonext });
    this.player = audio;
  }

  responsiveStyles() {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(`
      :host {
        display: block;
        width: 100%;
        height: 100%;
      }
      #player-container {
        width: 100%;
        height: 100%;
      }
      /* Circular audio player styles */
      .circular-player {
        width: 30px;
        height: 30px;
        min-width: 30px;
        min-height: 30px;
        border-radius: 50%;
        overflow: visible;
        aspect-ratio: 1;
        position: relative;
        cursor: pointer;
        display: flex;
        justify-content: center;
        align-items: center;
      }
      .progress-ring {
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        position: absolute;
        pointer-events: none;
      }
      .progress-ring circle {
        transition: stroke-dashoffset 0.1s linear;
      }
      .progress-ring circle[stroke] {
        pointer-events: all;
        cursor: pointer;
      }
      .progress-ring circle[fill] {
        pointer-events: none;
      }
      .play-pause-btn {
        background: none;
        border: none;
        cursor: pointer;
        outline: none;
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1;
        padding: 0;
      }
    `);
    return sheet;
  }
}

// Define the custom elements
window.customElements.define("w-audio", WAudio);
window.customElements.define("w-audio-c", WAudioCircular);
